/**
 * Analytics Scoping Model
 * Canonical definitions for data scoping across the application.
 *
 * Two independent dimensions determine what data is shown:
 * 1. VIEW — Who: AI + Community (platform-wide) vs My Stats (current user)
 * 2. FILTER — What: Everything vs Real Bets (vs All Tracked for user view)
 *
 * A third dimension controls how the data is displayed:
 * 3. ANALYSIS LEVEL — How: Legs (individual picks) vs Parlays (grouped bets)
 *
 * These concepts are reusable across any analytics feature.
 * See docs/analytics-scoping-model.md for full documentation.
 */

// =============================================================================
// View — Who's data are we looking at?
// =============================================================================

/** Platform-wide data vs user-specific data */
export type AnalyticsView = 'ai_community' | 'my_stats';

export const ANALYTICS_VIEWS: Record<AnalyticsView, { label: string; description: string }> = {
  ai_community: {
    label: 'AI + Community',
    description: 'All AI-generated picks plus community tracked bets across the platform',
  },
  my_stats: {
    label: 'My Stats',
    description: 'Only your personally tracked parlays and imported bets',
  },
};

// =============================================================================
// Filter — What subset of data within the view?
// =============================================================================

/** Filters narrow down data within a view */
export type AnalyticsFilter = 'everything' | 'real_bets' | 'all_tracked';

export const ANALYTICS_FILTERS: Record<AnalyticsFilter, { label: string; description: string }> = {
  everything: {
    label: 'All Picks',
    description: 'AI picks + imported real bets + manual bets (merged, no double-counting)',
  },
  real_bets: {
    label: 'Real Bets',
    description: 'Only bets imported from sportsbooks via SharpSports',
  },
  all_tracked: {
    label: 'All Tracked',
    description: 'Everything the user tracked (AI, manual, and imported bets)',
  },
};

// =============================================================================
// Analysis Level — How to display the data?
// =============================================================================

/** Controls whether calendar cells show leg-level or parlay-level stats */
export type AnalysisLevel = 'legs' | 'parlays';

export const ANALYSIS_LEVELS: Record<AnalysisLevel, { label: string; description: string }> = {
  legs: {
    label: 'Legs',
    description: 'Individual pick hit rates, over/under splits',
  },
  parlays: {
    label: 'Parlays',
    description: 'Parlay win rates, units won/lost',
  },
};

// =============================================================================
// Valid Combinations
// =============================================================================

/**
 * Not all view+filter combos are valid. This maps each view to its allowed filters.
 *
 * AI + Community:
 *   - Everything: AI picks (pick_predictions) + non-AI bets (parlays/legs with origin != 'ai')
 *   - Real Bets: Only imported sportsbook bets (parlays/legs with origin 'bet')
 *
 * My Stats:
 *   - All Tracked: User's tracked parlays of any origin (ai, manual, bet)
 *   - Real Bets: User's imported sportsbook bets only
 */
export const VALID_FILTERS_BY_VIEW: Record<AnalyticsView, AnalyticsFilter[]> = {
  ai_community: ['everything', 'real_bets'],
  my_stats: ['all_tracked', 'real_bets'],
};

/** Default filter when switching views */
export const DEFAULT_FILTER_BY_VIEW: Record<AnalyticsView, AnalyticsFilter> = {
  ai_community: 'everything',
  my_stats: 'all_tracked',
};

// =============================================================================
// API Scope — Derived from view + filter for backend queries
// =============================================================================

/**
 * The API scope is what gets sent to the backend. It's derived from the
 * view + filter combination. The backend uses this to decide which tables
 * and conditions to query.
 */
export type ApiScope = 'everything' | 'real_bets' | 'my_tracked' | 'my_real_bets';

/**
 * Derive the API scope from a view + filter combination.
 * This is the single source of truth for mapping UI state to API params.
 */
export function deriveApiScope(view: AnalyticsView, filter: AnalyticsFilter): ApiScope {
  if (view === 'ai_community') {
    return filter === 'everything' ? 'everything' : 'real_bets';
  }
  // my_stats
  return filter === 'real_bets' ? 'my_real_bets' : 'my_tracked';
}

// =============================================================================
// Data Source Mapping (for documentation / backend routing)
// =============================================================================

/** Filter for parlay origins — used by backend repository functions */
export type OriginsFilter = 'all' | 'bet' | 'non_ai';

/**
 * Documents which database tables and conditions each API scope maps to.
 * Used by backend route handlers to select the right repository functions.
 *
 * | API Scope     | Data Source                                          | Conditions                              |
 * |---------------|------------------------------------------------------|-----------------------------------------|
 * | everything    | pick_predictions + parlays/legs (originsFilter: non_ai) | Merged by date, no double-counting   |
 * | real_bets     | parlays/legs                                         | origins @> '{bet}' only                |
 * | my_tracked    | parlays/legs                                         | userId + any origin                    |
 * | my_real_bets  | parlays/legs                                         | userId + origins @> '{bet}' only       |
 */
export const API_SCOPE_DATA_SOURCES: Record<
  ApiScope,
  {
    usePickPredictions: boolean;
    useLegs: boolean;
    originsFilter: OriginsFilter;
    userScoped: boolean;
  }
> = {
  everything: {
    usePickPredictions: true,
    useLegs: true,
    originsFilter: 'non_ai',
    userScoped: false,
  },
  real_bets: {
    usePickPredictions: false,
    useLegs: true,
    originsFilter: 'bet',
    userScoped: false,
  },
  my_tracked: {
    usePickPredictions: false,
    useLegs: true,
    originsFilter: 'all',
    userScoped: true,
  },
  my_real_bets: {
    usePickPredictions: false,
    useLegs: true,
    originsFilter: 'bet',
    userScoped: true,
  },
};
